"use client";

import { useEffect, useRef, useState } from "react";
import { isRenderedElement } from "@/lib/ui/rendered-elements";

const OPEN_OVERLAY_SELECTOR = '[data-slot="drawer-overlay"][data-state="open"]';

function hasVisibleOpenDrawer() {
  const overlays = document.querySelectorAll<HTMLElement>(
    OPEN_OVERLAY_SELECTOR,
  );

  return Array.from(overlays).some((overlay) => {
    if (!overlay.isConnected) return false;
    const content = overlay.parentElement?.querySelector<HTMLElement>(
      '[data-slot="drawer-content"][data-state="open"]',
    );
    if (!isRenderedElement(overlay) || !isRenderedElement(content))
      return false;
    if (!content) return false;

    const overlayStyle = getComputedStyle(overlay);
    const contentStyle = getComputedStyle(content);
    return (
      overlayStyle.display !== "none" &&
      overlayStyle.visibility !== "hidden" &&
      contentStyle.display !== "none" &&
      contentStyle.visibility !== "hidden"
    );
  });
}

/**
 * Tracks whether any modal drawer overlay is currently open anywhere in the
 * document. Drawers portal to <body>, outside the app shell, so chrome like
 * the bottom navbar can't know about them through React context. Watching the
 * overlay elements' data attributes covers every drawer (vaul) without each
 * call site having to wire up state.
 */
export function useModalOverlayOpen(): boolean {
  const [isOpen, setIsOpen] = useState(false);
  const openRef = useRef(false);

  useEffect(() => {
    let frame: number | null = null;
    const update = () => {
      if (frame !== null) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        const next = hasVisibleOpenDrawer();
        // Our own chrome updates mutate the observed DOM. Do not enqueue a
        // React update for those mutations unless the visibility changed.
        if (next === openRef.current) return;
        openRef.current = next;
        setIsOpen(next);
      });
    };

    update();

    const observer = new MutationObserver(update);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["data-state", "class", "style"],
    });

    // iOS may restore a page from its back-forward cache without replaying
    // the drawer's React transition. Re-evaluate the live portal tree when the
    // page becomes active so a stale node can never strand the navbar hidden.
    window.addEventListener("pageshow", update);
    document.addEventListener("visibilitychange", update);

    return () => {
      observer.disconnect();
      window.removeEventListener("pageshow", update);
      document.removeEventListener("visibilitychange", update);
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, []);

  return isOpen;
}
